import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/google-sign-in-button";
import { useSignInProviders, type GoogleSignInFailure, type PasswordSignInFailure } from "@/api/sign-in";
import { useIdentity } from "@/context/identity-context";
import { useLocalization } from "@/context/localization-context";
import { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const GOOGLE_FAILURE_KEYS = {
  wrong_domain: "login.google.wrongDomain",
  account_disabled: "login.google.disabled",
  unavailable: "login.google.unavailable",
  failed: "login.google.failed",
} as const;

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const { login, loginWithGoogle } = useIdentity();
  const { t } = useLocalization();
  const { data: providers } = useSignInProviders();
  const google = providers?.google ?? null;
  const [loading, setLoading] = useState(false);
  // the reason rather than the text, so that the message follows a language switch
  const [failed, setFailed] = useState<PasswordSignInFailure | null>(null);
  const [googleFailure, setGoogleFailure] = useState<GoogleSignInFailure | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setFailed(null);
    setGoogleFailure(null);
    const formData = new FormData(event.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const failure = await login(email, password);
    if (failure) {
      setFailed(failure);
    } else {
      navigate(location.pathname);
    }
    setLoading(false);
  };

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      setLoading(true);
      setFailed(null);
      setGoogleFailure(null);
      const failure = await loginWithGoogle(credential);
      if (failure) {
        setGoogleFailure(failure);
      } else {
        navigate(location.pathname);
      }
      setLoading(false);
    },
    [loginWithGoogle, navigate, location.pathname],
  );

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("login.form.title")}</CardTitle>
          <CardDescription>{t("login.form.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              {failed && (
                <div className="text-red-500">
                  {failed === "use_google" ? t("login.form.useGoogle") : t("login.form.failed")}
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="email">{t("login.form.email")}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="m@example.com"
                  required
                  disabled={loading}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">{t("login.form.password")}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  disabled={loading}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? t("login.form.submitting") : t("login.form.submit")}
              </Button>
            </div>
          </form>
          {google && (
            <div className="mt-6 flex flex-col gap-4">
              <div className="relative text-center text-xs uppercase text-muted-foreground after:absolute after:inset-x-0 after:top-1/2 after:border-t after:border-border">
                <span className="relative z-10 bg-card px-2">{t("login.form.or")}</span>
              </div>
              {googleFailure && (
                <div className="text-red-500">
                  {t(GOOGLE_FAILURE_KEYS[googleFailure], { domain: google.domain })}
                </div>
              )}
              <GoogleSignInButton provider={google} onCredential={handleGoogleCredential} disabled={loading} />
              <p className="text-center text-xs text-muted-foreground">
                {t("login.google.hint", { domain: google.domain })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
